/* eslint-disable no-invalid-this */
import {t} from "@lingui/core/macro";
import _ from "underscore";

import {IntegerField} from "@khanacademy/event-schemas";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import Log, {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import type {
    UpdateChallengeProgressData,
    UpdateChallengeProgressVariables,
} from "#content-library/queries.ts";
import {mapToUserProgram} from "#content-library/types/gql-type-mappers.ts";
import $ from "#jquery/jquery.ts";
import {PROGRAM_STATUS} from "#scratchpad-shared-package/types.ts";
import type {LiveEditor} from "#scratchpads-package/types.ts";
import Backbone from "#shared-package/backbone-lite.js";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import hintPrettify from "./challenge-hint-prettify.ts";

type FeedbackType =
    | "editor"
    | "translated"
    | "syntax"
    | "hintcode"
    | "inability"
    | "grader"
    | "instructions"
    | "other";

// TODO(LP-12309): Update to specific typing when converting to GQL
type Response = any;
/*
 * Returns true if the array of test states is a sequence of passes followed by a sequence of failures
 */
const isPassedThenFails = function (testStates: any) {
    // Check if the array starts with some sequence of passes, and then
    // some sequence of fails, and that there are only these two groups
    //  (ie, that every pass occurs before the first fail)
    return (
        testStates.length > 0 &&
        testStates[0] === "pass" &&
        _.contains(testStates, "fail") &&
        _.indexOf(testStates, "fail") > _.lastIndexOf(testStates, "pass")
    );
};

export type ScratchpadChallengeModel = BackboneModel<
    Backbone.Model,
    {
        initialize: (options: any) => void;
        editor: LiveEditor;
        setLiveEditor: (liveEditor: LiveEditor) => void;
        triggerUpdate: () => void;
        completeChallenge: () => void;
        onGoalStep: () => boolean;
        startOver: () => void;
        moveToStep: (i: number) => void;
        assertionsFail: () => boolean;
        allTestsPass: () => boolean;
        firstFailure: () => number;
        previousStepFailed: () => boolean;
        currentStepHasWarning: () => boolean;
        getDebugInfo: () => string;
        processResults: (results: any) => void;
        getCurrentTestGroup: () => any;
        canBeCompleted: () => boolean;
        currentStepComplete: () => boolean;
        hasSyntaxErrors: () => boolean;
        liveEditor: LiveEditor;
        onComplete?: () => unknown;
        onStart?: () => unknown;
        updateChallengeProgress: (
            arg1: UpdateChallengeProgressVariables["stashedCode"],
            arg2: UpdateChallengeProgressVariables["status"],
        ) => Promise<UpdateChallengeProgressData>;
    },
    Empty
>;

export const ScratchpadChallenge: ScratchpadChallengeModel =
    Backbone.Model.extend(
        {
            initialize: function (this: any, options: any) {
                this.userScratchpad = options.userScratchpad;
                this.editor = options.editor;
                this.useBlocks = options.useBlocks;
                this.contentType = options.contentType;
                this.onComplete = options.onComplete;
                this.onStart = options.onStart;
                this.lessonId = options.lessonId;

                // To replace API requests from within backbone, we accept
                // an updater function to handle the network requests for
                // recording progress/changes on the Challenge content.
                this._updateChallengeProgress = options.updateChallengeProgress;

                const points = this.userScratchpad.get("points");
                // See scratchpads/models.py
                // Note that userScratchpad may not be fully initialized at this point
                const complete =
                    this.userScratchpad.get("challengeStatus") ===
                    PROGRAM_STATUS.CHALLENGE_COMPLETE;
                // That means that "complete" may be set to false here,
                // even if the eventually loaded UserScratchpad has challengeStatus = 1
                this.set("complete", complete);
                // hack hacky hack -- we don't know how many tests we have
                // but if they completed the challenge, we want them to start on the goal page
                this.set(
                    "lastCompleted",
                    complete ? Number.POSITIVE_INFINITY : -1,
                );
                this.set("step", complete ? Number.POSITIVE_INFINITY : 0);
                this.set("points", points);
                this.on("change:complete", (model, complete) => {
                    if (complete) {
                        this.updateChallengeProgress(
                            this.userScratchpad.get("stashedCode"),
                            PROGRAM_STATUS.CHALLENGE_COMPLETE,
                        )
                            .then(() => this.onComplete?.())
                            .catch((e) => {
                                Log.error(
                                    "Error saving challenge status",
                                    Errors.Internal,
                                    {cause: e},
                                );
                            });
                    }
                });
            },

            // NOTE: this only works because LiveEditor is untyped
            editor: null as LiveEditor,

            setLiveEditor: function (this: any, liveEditor: LiveEditor) {
                this.liveEditor = liveEditor;
                this.editor = liveEditor.editor;
            },

            triggerUpdate: function (this: any) {
                this.trigger("challengeUpdate", this);
            },

            completeChallenge: function (this: any) {
                const totalSteps = this.get("tests").length;
                this.set("complete", true);
                // the goal tab is treated like a hidden last step
                this.set("step", totalSteps);
                this.set("lastCompleted", totalSteps);
                this.triggerUpdate();

                const contentId = this.userScratchpad.get("scratchpadId");
                if (contentId != null) {
                    const totalStepsInt = new IntegerField(totalSteps);
                    publishAnalyticsEventForMinorConversion({
                        eventType: "ComputingChallengeStepCompleted",
                        eventSchemaVersion: 2,
                        stepNumber: totalStepsInt,
                        totalSteps: totalStepsInt,
                        contentId,
                    });
                    publishAnalyticsEventForMinorConversion({
                        eventType: "ComputingChallengeCompleted",
                        eventSchemaVersion: 2,
                        totalSteps: totalStepsInt,
                        contentId,
                    });
                }
            },

            onGoalStep: function (this: any) {
                return this.get("step") >= this.get("tests").length;
            },

            startOver: function (this: any) {
                this.updateChallengeProgress(
                    "",
                    PROGRAM_STATUS.CHALLENGE_STARTED,
                )
                    .then((data) => {
                        this.moveToStep(0);
                        this.onStart?.();
                    })
                    .catch((e) => {
                        Log.error(
                            "failed to restart Challenge",
                            Errors.TransientKhanService,
                            {
                                cause: e,
                                sentryData: {
                                    contexts: {
                                        extras: {
                                            contentId:
                                                this.userScratchpad.get(
                                                    "scratchpadId",
                                                ),
                                            lessonId: this.lessonId,
                                        },
                                    },
                                },
                            },
                        );
                    });
            },

            moveToStep: function (this: any, i: number) {
                if (i === -1) {
                    // This happens in the case of a student who completes future steps
                    // but never clicked "Next step" to see the instructions
                    this.set("step", this.get("step") + 1);
                } else {
                    this.set("step", i);
                }
                this.triggerUpdate();
                this.editor.focus();

                const contentId = this.userScratchpad.get("scratchpadId");
                if (contentId != null) {
                    const totalSteps = this.get("tests").length;
                    publishAnalyticsEventForMinorConversion({
                        eventType: "ComputingChallengeStepCompleted",
                        eventSchemaVersion: 2,
                        stepNumber: new IntegerField(i),
                        totalSteps: new IntegerField(totalSteps),
                        contentId,
                    });
                }
            },

            assertionsFail: function (this: any) {
                return (
                    this.get("assertions") && this.get("assertions").length > 0
                );
            },

            // Returns false if the tests are undefined or if they don't all pass
            allTestsPass: function (this: any) {
                return !!(
                    this.get("tests") &&
                    this.get("tests").every((test) => {
                        return test.state === "pass";
                    })
                );
            },

            firstFailure: function (this: any) {
                return _.pluck(this.get("tests"), "state").indexOf("fail");
            },

            previousStepFailed: function (this: any) {
                const firstFailure = this.firstFailure();
                return firstFailure >= 0 && firstFailure < this.get("step");
            },

            currentStepHasWarning: function (this: any) {
                const currentResults =
                    this.get("tests")[this.get("step")]["results"];
                return !!_.find(currentResults, function (result) {
                    return result.meta.alsoMessage;
                });
            },

            getDebugInfo: function (this: any) {
                return [
                    "complete: " + this.get("complete"),
                    "lastCompleted: " + this.get("lastCompleted"),
                    "points: " + this.get("points"),
                    "step: " + this.get("step"),
                ].join("\n");
            },

            processResults: function (this: any, results: any) {
                if (
                    this.userScratchpad.get("challengeStatus") ===
                    PROGRAM_STATUS.CHALLENGE_NOT_STARTED
                ) {
                    this.updateChallengeProgress(
                        this.userScratchpad.get("stashedCode"),
                        PROGRAM_STATUS.CHALLENGE_STARTED,
                    )
                        .then(() => this.onStart?.())
                        .catch((e) => {
                            Log.error(
                                "failed to update challenge progress: " + e,
                                Errors.Internal,
                                {cause: e},
                            );
                        });
                }

                // If the test results and errors are the same, it's silly
                //  to go through the work of triggering an update and re-rendering
                if (
                    _.isEqual(this.get("tests"), results.tests) &&
                    _.isEqual(this.get("assertions"), results.assertions) &&
                    _.isEqual(this.get("errors"), results.errors)
                ) {
                    return;
                }

                const tests = results.tests;
                this.set("tests", tests);
                this.set("errors", results.errors);
                this.set("assertions", results.assertions);

                fetchColoredText(
                    tests,
                    _.bind(function (this: any) {
                        if (!this.get("complete")) {
                            const states = _.map(tests, function (test) {
                                return test.state;
                            });
                            if (
                                isPassedThenFails(states) &&
                                !this.assertionsFail()
                            ) {
                                const currentIndex =
                                    _.indexOf(states, "fail") - 1;

                                if (currentIndex > this.get("lastCompleted")) {
                                    this.set("lastCompleted", currentIndex);
                                    this.set("step", currentIndex);
                                }
                            }
                        }
                        this.triggerUpdate();
                    }, this),
                    this,
                );
            },

            getCurrentTestGroup: function (this: any) {
                return this.get("tests")[this.get("step")];
            },

            canBeCompleted: function (this: any) {
                return (
                    !this.get("complete") &&
                    !this.assertionsFail() &&
                    this.allTestsPass()
                );
            },

            currentStepComplete: function (this: any) {
                return (
                    !this.get("complete") &&
                    this.get("lastCompleted") >= this.get("step") &&
                    this.getCurrentTestGroup().state === "pass"
                );
            },

            hasSyntaxErrors() {
                return !this.liveEditor || !!this.liveEditor.hasErrors();
            },

            liveEditor: null,

            updateChallengeProgress: function (
                this: any,
                stashedCode: UpdateChallengeProgressVariables["stashedCode"],
                status: UpdateChallengeProgressVariables["status"],
            ): Promise<UpdateChallengeProgressData> {
                if (!this._updateChallengeProgress) {
                    return Promise.reject(
                        new KAError(
                            "no handler set for updating Challenge progress",
                            Errors.Internal,
                        ),
                    );
                }

                const input: UpdateChallengeProgressVariables = {
                    programId: this.userScratchpad.get("scratchpadId"),
                    lessonId: this.lessonId,
                    stashedCode,
                    status,
                };

                return this._updateChallengeProgress(input).then((response) => {
                    const data = response.data.updateChallengeProgress;
                    if (data.error?.code) {
                        return new KAError(
                            data.error?.code,
                            Errors.GraphqlResponse,
                        );
                    }
                    // If an error is absent, we expect programProgress to
                    // always return. Throw an error if it doesn't.
                    if (!data.programProgress) {
                        return new KAError(
                            "programProgress returned null without error code",
                            Errors.GraphqlResponse,
                        );
                    }
                    // Update both the local state and the userScratchpad
                    this.set("points", data.programProgress.points);
                    this.userScratchpad.set(
                        mapToUserProgram(data.programProgress),
                    );

                    return data;
                });
            },
        },
        // TypeScript requires classProperties to properly type check Backbone.Models
        {},
    );

/*
 * This is a mild refactoring of submitForm() from the ChallengeFeedbackModal
 * backbone model that has since been replaced with challenge-feedback.
 * It takes some feedback from the modal and sends it to Google Sheets.
 * @param {ScratchpadChallenge} challenge - the current challenge
 * @param {String} feedbackType - a slug describing the type of feedback.
 * @param {Array[{name: String, value: String}]} responses - an array
 *      of feedback responses with a name (like a question slug) and a value
 *      (an answer slug).
 * @param {String} details - what the user wrote in the "more details" box.
 * @returns {Promise} - a promise from the request.
 */
export function sendContentFeedback(
    // @ts-expect-error - TS2749 - 'ScratchpadChallenge' refers to a value, but is being used as a type here. Did you mean 'typeof ScratchpadChallenge'?
    challenge: ScratchpadChallenge,
    profile: ProfileModelType | null,
    feedbackType: FeedbackType,
    responses: Array<Response> | null | undefined,
    details: string | null | undefined,
    dontSendToContentIssueTracker: boolean,
): Promise<Response> {
    responses = responses || [];
    details = details || "";
    // Put Q+A feedback in the expected format
    const allFeedback = responses
        .map((response) => {
            const key = Object.keys(response)[0];
            const value = response[key];
            return `${key}: ${value}`;
        })
        .concat([details])
        .join("\n");
    // Issue description
    const issueDescription = challenge.liveEditor
        ? allFeedback +
          "\n\n" +
          "{code:JavaScript}" +
          challenge.liveEditor.editor.text() +
          "{code}\n\n" +
          challenge.getDebugInfo()
        : "";
    // Challenge ID and URL
    // Again, there has to be a better way.
    // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
    // eslint-disable-next-line no-restricted-syntax
    const challengeSlug = window.location.href.substr(
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax
        window.location.href.lastIndexOf("/") + 1,
    );
    // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
    // eslint-disable-next-line no-restricted-syntax
    const challengeUrl = window.location.href;
    const issueType = {
        editor: "The editor is broken" as const,
        translated: "Not translated" as const,
        syntax: "Syntax error won't go away" as const,
        hintcode: "Question or hints confusing" as const,
        inability: "Videos didn't prepare adequately" as const,
        grader: "Grader won't accept code" as const,
        instructions: "Question or hints confusing" as const,
        other: "Other" as const,
    }[feedbackType];

    // there was no original call to mark conversions here
    // we want to publish a new analytic event here w/o LT context
    // so we will go with sending a minor conversion
    return publishAnalyticsEventForMinorConversion({
        eventType: "ChallengeIssueSubmit",
        eventSchemaVersion: 2,
        description: issueDescription,
        challengeUrl: challengeUrl,
        challengeSlug: challengeSlug,
        type: issueType,
        userAgent: navigator.userAgent,
        locale: RequestInfo.kaLocale,
    });
}

/*
 * Probably a good idea to keep the # of congrats and motivational messages
 * off by one to maximize the number of unique messages before repeating
 * as they will not be reset as long as the user keeps progressing
 * through a tutorial and does not refresh the page
 */

let messageCounter = -1;

export const getNextProgressMessage = function (): string {
    const congratsMessages = _.shuffle([
        t`Way to go!`,
        t`Awesome!`,
        t`Good job!`,
        t`Nice work!`,
        t`Amazing!`,
        t`Brilliant!`,
        t`Incredible!`,
        t`Making progress!`,
        t`Getting closer!`,
    ]);
    const motivationMessages = _.shuffle([
        t`Moving forward!`,
        t`Ready for more?`,
        t`Onto the next step!`,
        t`Keep it up!`,
        t`You can do it!`,
        t`Great things are ahead!`,
        t`Keep moving on!`,
    ]);
    messageCounter += 1;
    const line1 = congratsMessages[messageCounter % congratsMessages.length];
    const line2 =
        motivationMessages[messageCounter % motivationMessages.length];
    return line1 + " " + line2;
};

let completionMessageCounter = -1;

export const getNextCompletionMessage = function (): any {
    const completionMessages = _.shuffle([
        t`All steps complete!`,
        t`Great job!`,
        t`You did it!`,
        t`You figured it out!`,
    ]);
    completionMessageCounter += 1;
    return completionMessages[
        completionMessageCounter % completionMessages.length
    ];
};

// This is horrible
const prettifiedCache: Record<string, string> = {};
let taskCounter = 0;
let activeTask = -1;

/*
 * Fetches and caches pretty SJS patterns
 */
const fetchColoredText = function (tests: any, onFinish: any, challenge: any) {
    // the number of outstanding tasks. we start at 1 so if a task in the inner loop
    // immediately finishes, it won't call the completion callback.
    let numMissing = 1;
    // The type of scratchpad that we're dealing with
    const type = challenge.contentType;
    const taskId = taskCounter;
    taskCounter += 1;
    activeTask = taskId;

    for (let i = 0; i < tests.length; i++) {
        const results = tests[i].results;
        for (let j = 0; j < results.length; j++) {
            if (results[j].meta.structure) {
                const structure = results[j].meta.structure;
                if (
                    !prettifiedCache.hasOwnProperty(JSON.stringify(structure))
                ) {
                    numMissing += 1;
                    // fun fact: sometimes this is synchronous and sometimes its not, hence the
                    // need for the numMissing = 1 hack at the top.
                    markupStructure[type](
                        structure,
                        function (formatted) {
                            prettifiedCache[JSON.stringify(structure)] =
                                formatted;
                            numMissing -= 1;
                            // if we're done AND we're the currently active task
                            if (numMissing <= 0 && taskId === activeTask) {
                                onFinish();
                            }
                        },
                        challenge,
                    );
                }
            }
        }
    }

    // decrement to account for initialization at 1
    numMissing -= 1;

    if (numMissing === 0) {
        onFinish();
    }
};

export const getColoredTextForTest = function (test: any): any {
    return prettifiedCache[JSON.stringify(test.meta.structure)] || "";
};

const markupStructure = {
    pjs: function (
        fnText: string,
        callback: (arg1: string) => void,
        challenge: any,
    ) {
        if (challenge.useBlocks) {
            const blockHTML =
                challenge.liveEditor.editor.editor.codeToHTML(fnText);
            const html = $("<div>")
                .append($("<div>").addClass("rainbowjs").html(blockHTML))
                .html();
            callback(html);
            return;
        }

        const structure = fnText.slice(
            fnText.indexOf("{") + 1,
            fnText.lastIndexOf("}"),
        );

        hintPrettify(structure, callback);
    },

    sql: function (hint: string, callback: (arg1: string) => void) {
        let wrapped = "";
        if (hint) {
            wrapped = $("<div></div>")
                .append($('<pre class="rainbowjs"></pre>').text(hint))
                .html();
        }
        callback(wrapped);
    },

    webpage: function (hint: string, callback: (arg1: string) => void) {
        let wrapped = "";
        if (hint) {
            // TODO(bbondy): There is no built in support for SQL, but we
            // could possibly add something like this:
            // https://gist.github.com/raymyers/3881216
            wrapped = $("<div></div>")
                .append($('<pre class="rainbowjs"></pre>').text(hint))
                .html();
        }
        callback(wrapped);
    },
} as const;
